import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Echarts,
  Icon,
  PageBar,
  PopOver,
  OverlayTrigger,
  Column,
  Table,
  Checkbox,
  TimePicker,
} from '@tencent/spaui';
import { message, Toolbar } from '@tencent/ptp-fe-common';
import _ from 'lodash';
import {
  gridStyle,
  multipleColor,
  fullChartStyle,
} from './index';
import { RPCFactory } from '../../../utils/RequestUtil';
import { isLeadsRole } from "../../../utils/routes";
import { beaconInstance } from '../../../instrumentation/beaconSetup';
import { PieChart } from './pieChart';
import { BarChart } from './barChart';

const LeadsFollowAverageChart = (props) => {
  const [employeeNames, setEmployeeNames] = useState([]);
  const [employeeFollowValues, setEmployeeFollowValues] = useState([]);
  useEffect(() => {
    const entities =
      props.target === 'USER' ? props.followTimeDataList[0].entities : props.followTimeDataList[1].entities;
    const afterSortEntities = entities.sort((a, b) => b.value - a.value);
    const names = afterSortEntities.map((item) => item.label);
    const values = afterSortEntities.map((item) => (item.value / 60).toFixed(2));
    setEmployeeNames(names);
    setEmployeeFollowValues(values);
  }, [props.followTimeDataList, props.target]);
  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: {
      type: 'category',
      data: employeeNames,
    },
    yAxis: {
      type: 'value',
      name: '(小时)',
      axisLabel: {
        formatter: '{value}',
      },
    },
    grid: gridStyle,
    dataZoom: {
      startValue: 0,
      endValue: 10,
      type: 'slider',
      height: 10,
    },
    series: [
      {
        data: employeeFollowValues,
        type: 'bar',
        barWidth: '20%',
        color: multipleColor[0],
        label: {
          show: false,
        },
      },
    ],
  };

  return <Echarts {...option} style={fullChartStyle} />;
};
const initData = [
  {
    userName: '', // 员工姓名（维度为USER时使用）
    groupName: '', // 团队名称（维度为USER_GROUP时使用）
    leadsCount: 0, // 外呼线索量
    totalCallNum: 0, // 拨打电话总量
    connectCallNum: 0, // 接通电话量
    leadsCallNumAvg: 0, // 每条线索平均外呼量
    totalDurationMinutes: 0, // 通话总时长
    leadsDurationAvgMinutes: 0, // 每条线索平均通话时长
  },
];
const EmployeeFollowTable = (props) => {
  const [displayTable, setDisplay] = useState(true);
  const [sortName, setSortName] = useState(['leadsCount']);
  const [sortOrder, setSortOrder] = useState({ leadsCount: 'desc' });
  const [totalTableData, setTotalTableData] = useState(initData);
  const [pageValue, setPageValue] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const entities = props.target === 'USER' ? props.callInfoDataList[0].entities : props.callInfoDataList[1].entities;
    setTableDataByNameAndOrder(entities, ['leadsCount'], { leadsCount: 'desc' });
  }, [props.callInfoDataList, props.target]);

  // 需求中要求将【汇总】行放在第一页的第一行
  const sortedTableData = useMemo(() => {
    if (totalTableData.length < 1) return totalTableData;
    const totalItemIndex = totalTableData.findIndex((item) => item.userName === '汇总');
    if (totalItemIndex < 0) return totalTableData;
    const arr = [...totalTableData];
    const [totalItem] = arr.splice(totalItemIndex, 1);
    arr.unshift(totalItem);
    return arr;
  }, [totalTableData, pageSize]);

  const displayTableData = useMemo(() => {
    const paged = sortedTableData.slice((pageValue - 1) * pageSize, pageValue * pageSize);
    return paged;
  }, [sortedTableData, pageSize, pageValue]);

  const setTableDataByNameAndOrder = (totalTableData, sortName, sortOrder) => {
    const newData = totalTableData.sort((firstEle, secondEle) => {
      for (let index = 0; index < sortName.length; index++) {
        const sortItem = sortName[index];
        const sortMethod = sortOrder[sortItem];
        if (firstEle[sortItem] !== secondEle[sortItem]) {
          return sortMethod === 'asc'
            ? firstEle[sortItem] - secondEle[sortItem]
            : secondEle[sortItem] - firstEle[sortItem];
        }
      }
    });
    setTotalTableData([...newData]);
  };
  const handleSort = (event, sortName, sortOrder) => {
    setSortName(sortName);
    setSortOrder(sortOrder);
    setTableDataByNameAndOrder(totalTableData, sortName, sortOrder);
  };

  const changeDisplayStatus = () => {
    setDisplay(!displayTable);
  };
  const renderName = (type, name, value, data) => {
    if (type !== 'head') {
      return <div>{props.target === 'USER' ? data.userName : data.groupName}</div>;
    }
    return <div>{props.target === 'USER' ? '员工姓名' : '团队姓名'}</div>;
  };
  const renderHeaderWithTip = (props, tip, fixedValue) => {
    const popover = <PopOver>{tip}</PopOver>;
    if (props[0] == 'head') {
      return (
        <div>
          {props[2]}
          <OverlayTrigger trigger="hover" delay={100} overlay={popover}>
            <i className="icon icon-help table-icon">
              <i></i>
            </i>
          </OverlayTrigger>
        </div>
      );
    }
    return <div>{fixedValue ? props[2].toFixed(2) : props[2]}</div>;

  };
  const handlePageChange = (event, value) => {
    setPageValue(value);
  };

  const handlePageSizeChange = (value, pageSize) => {
    // 切换每页数量
    setPageValue(value);
    setPageSize(pageSize);
  };

  const popover = <PopOver>筛选条件下，统计员工历史外呼行为数据，评判员工的外呼工作量</PopOver>;
  return (
    <div className="employee-follow-table">
      <div className="chart-title clickable" onClick={changeDisplayStatus}>
        员工外呼工作量
        {displayTable && <Icon type="arrow-up" width="8" height="8" className="arrow-up" />}
        {!displayTable && <Icon type="arrow-down" width="8" height="8" className="arrow-down" />}
        <OverlayTrigger trigger="hover" delay={100} overlay={popover}>
          <i className="icon icon-help table-icon">
            <i></i>
          </i>
        </OverlayTrigger>
      </div>
      {displayTable && (
        <div>
          <Table
            data={displayTableData}
            multiFieldSort
            // maxMultiFieldSortSize={2}
            sortName={sortName}
            sortOrder={sortOrder}
            onSort={handleSort}
            title="员工线索跟进工作量"
            maxWidth={1200}
          >
            <Column name="name" maxWidth="300" render={renderName}>
              员工姓名
            </Column>
            <Column
              name="leadsCount"
              maxWidth="300"
              render={(...props) => renderHeaderWithTip(props, '筛选条件下，该员工外呼过的线索数量')}
              sortable
            >
              外呼线索量
            </Column>
            <Column
              name="totalCallNum"
              maxWidth="300"
              render={(...props) => renderHeaderWithTip(props, '筛选条件下，该员工外呼线索的次数')}
              sortable
            >
              拨打电话总量
            </Column>
            <Column
              name="connectCallNum"
              maxWidth="300"
              render={(...props) => renderHeaderWithTip(props, '筛选条件下，该员工外呼线索中接通状态=已接通的次数')}
              sortable
            >
              接通电话量
            </Column>
            {/* <Column
              name="leadsCallNumAvg"
              maxWidth="300"
              render={(...props) => renderHeaderWithTip(props, '所选条件下，平均每条线索的外呼电话次数')}
              sortable
            >
              每条线索平均外呼量
            </Column> */}
            <Column
              name="totalDurationMinutes"
              maxWidth="300"
              render={(...props) => renderHeaderWithTip(props, '筛选条件下，该员工外呼线索中接通状态=已接通的总通话时长', 2)}
              sortable
            >
              通话总时长（分钟）
            </Column>
            {/* <Column
              name="leadsDurationAvgMinutes"
              maxWidth="300"
              render={(...props) => renderHeaderWithTip(props, '所选条件下，每条已接通线索的平均通话时长', 2)}
              sortable
            >
              每条线索平均通话时长
            </Column> */}
          </Table>
          <PageBar
            value={pageValue}
            pageSize={pageSize}
            pageSizeList={[10, 20, 50]}
            totalSize={totalTableData.length}
            onChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
    </div>
  );
};

const FollowUpStatus = {
  0: '未接通',
  1: '待联系',
  2: '已接通',
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const LeadsFollowChart = (props) => {
  const [followData, setFollowData] = useState({ overallData: {}, trendData: {} });
  const [employeePerformanceData, setEmployeePerformanceData] = useState({
    followTimeDataList: [
      { dimension: 'USER', entities: [] },
      { dimension: 'USER_GROUP', entities: [] },
    ],
    callInfoDataList: [
      { dimension: 'USER', entities: [] },
      { dimension: 'USER_GROUP', entities: [] },
    ],
  });
  const previousParams = usePrevious(props.queryParams);
  const [target, setTarget] = useState('USER');
  // 设置工作时间开关配置
  const [onlyWorkTime, setOnlyWorkTime] = useState(false);
  const defaultStartTime = '08:00';
  const defaultEndTime = '18:00';
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);

  // 比较两个时间的大小
  const CompareDate = (t1, t2) => {
    const date = new Date();
    const a = t1.split(":");
    const b = t2.split(":");
    return date.setHours(a[0], a[1]) - date.setHours(b[0], b[1]);
  }
  const formatHourTime = (time) => Number(time.replace(":00", ""));
  // 初始化人效数据
  const initEmployeePerformance = () => {
    const beginHour = formatHourTime(defaultStartTime)
    const endHour = formatHourTime(defaultEndTime);
    // const isManager = isLeadsRole('管理员', '超级管理员');
    const isManager = true; // 当前页面仅管理员可看，不用判断
    const params = isManager ? {
      ...props.queryParams,
      beginHour,
      endHour
    } : props.queryParams;
    // props.setExtraParams({ beginHour, endHour });
    RPCFactory.leadsReport
    .getEmployeePerformance(params)
    .done((data) => {
      setEmployeePerformanceData(data);
    })
    .fail((ret, msg) => {
      alert(msg);
    });
  }
  useEffect(() => {
    if (props.queryParams.startCreateTime && !_.isEqual(props.queryParams, previousParams)) {
      if (props.queryParams.ownerIds) {
        setTarget('USER');
      }
      RPCFactory.leadsReport
        .getLeadsFollow(props.queryParams)
        .done((data) => {
          setFollowData(data);
          props.setLeadsFollowData(data.trendData);
        })
        .fail((ret, msg) => {
          alert(msg);
        });
      RPCFactory.leadsReport
        .getEmployeeSettingInfo()
        .done((data) => {
          const { beginHour = 0, endHour = 0 } = data;
          setOnlyWorkTime(beginHour>0 && endHour>0);
          initEmployeePerformance();
        })
        .fail((ret, msg) => {
          alert(msg);
        });
    }
  }, [props.queryParams]);

  const [employeeNameList, setEmployeeNameList] = useState([]);
  const [employeeValueList, setEmployeeValueList] = useState([]);
  useEffect(() => {
    let index = 1;
    if (target === 'USER') {
      index = 0;
    }
    if (followData.detailDataList && followData.detailDataList[index].entities) {
      setEmployeeNameList(
        followData.detailDataList[index].entities.map((item) => ({
          value: item.label,
          textStyle: { fontSize: 10 },
        })),
      );
      setEmployeeValueList(processData(followData.detailDataList[index].entities));
    }
  }, [target, followData]);
  const processData = (dataList) => {
    const dataMaps = Object.keys(FollowUpStatus).map((key) => ({
      data: dataList.map((item) => item.subItems.find((item) => item.label === FollowUpStatus[key]).value),
      name: FollowUpStatus[key],
      color: multipleColor[key],
    }));
    return dataMaps;
  };
  const switchTargetTo = (target) => {
    setTarget(target);
    beaconInstance.onUserAction('employChart_switch_follow_target', {
      accountId: window.pageProps.uid,
      companyName: window.pageProps.companyName,
      target,
    });
  };

  const popover = <PopOver>所选条件下，有线索的员工跟进线索的时效性（线索分配/认领时间与首次外呼时间的时间间隔平均值）</PopOver>;

  const workTimeCheckProps = {
    label: '仅查看工作时间',
    tip: '开启按钮后，只统计客服对工作时间内生成的线索的平均响应时长。',
    value: 'checkbox',
    checked: !!onlyWorkTime,
    onChange: (event, value) => {
      setOnlyWorkTime(value)
      // 开启时默认拉取数据
      setStartTime(defaultStartTime);
      setEndTime(defaultEndTime);
      value ? refreshPerformance(defaultStartTime, defaultEndTime)
        : refreshPerformance("00:00", "00:00");
    },
    canCancelCheck: true,
    OverlayTriggerProps: { trigger: 'hover', delay: 100 },
  }
  // 刷新图标接口
  const refreshPerformance = (startTime, endTime) => {
    const params = props.queryParams;
    if (CompareDate(startTime, endTime) > 0) {
      message.warning("起始时间不能大于截止时间")
      return;
    }
    if (startTime) {
      params.beginHour = formatHourTime(startTime);
    }
    if (endTime) {
      params.endHour = formatHourTime(endTime);
    }
    // props.setExtraParams({ beginHour: params.beginHour, endHour: params.endHour });
    RPCFactory.leadsReport.getEmployeePerformance(params)
    .done((data) => {
      setEmployeePerformanceData(data);
    })
    .fail((ret, msg) => {
      alert(msg);
    });
  }
  return (
    <div className="follow-analysis">
      <div className="display-flex">
        <PieChart
          overallData={followData.overallData}
          total={followData.trendData.total}
          title="跟进状态分布（单位：条）"
        />
        <div className="space-divider"/>
        <BarChart
          title="员工线索跟进情况"
          target={target}
          setTarget={switchTargetTo}
          xAxisData={employeeNameList}
          yAxisData={employeeValueList}
          hideSwitcher={!!props.queryParams.ownerIds}
        />
      </div>
      <div className="height-20"/>
      <Toolbar
        className="chart-toolbar"
        left={<div className="chart-title">
          平均线索响应时长
          <OverlayTrigger trigger="hover" delay={100} overlay={popover}>
            <i className="icon icon-help table-icon">
              <i/>
            </i>
          </OverlayTrigger>
        </div>}
        right={
          <div className="chart-control">
            {/* by fightingliu */}
            {
              // isLeadsRole('管理员', '超级管理员') &&
              <Checkbox
              {...workTimeCheckProps}
              />
            }
            <div className="chart-date">
              {onlyWorkTime && <div className="chart-time">
                <TimePicker
                  placeholder="请选择时间"
                  value={startTime}
                  config={{
                    hour: true
                  }}
                  onChange={(e, value) => {
                    if (startTime !== value) {
                      setStartTime(value);
                      refreshPerformance(value, endTime);
                    }
                  }}
                />
                <div className="time-split"/>
                <TimePicker
                  placeholder="请选择时间"
                  value={endTime}
                  config={{
                    hour: true
                  }}
                  onChange={(e, value) => {
                    if (endTime !== value) {
                      setEndTime(value);
                      refreshPerformance(startTime, value);
                    }
                  }}
                />
              </div>}
            </div>
          </div>
        }
      />
      <LeadsFollowAverageChart followTimeDataList={employeePerformanceData.followTimeDataList} target={target} />
      <EmployeeFollowTable callInfoDataList={employeePerformanceData.callInfoDataList} target={target} />
    </div>
  );
};
